const SVG_NS = "http://www.w3.org/2000/svg";

function createSvgPath(d) {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.style.position = "absolute";
  svg.style.width = "0";
  svg.style.height = "0";
  svg.style.overflow = "hidden";
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", d);
  svg.appendChild(path);
  document.body.appendChild(svg);
  return { svg, path };
}

/**
 * Splits a `d` attribute into individual subpath strings (one per M command).
 */
export function getSubpaths(d) {
  if (!d) return [];
  const parts = d.split(/(?=M)/gi).filter((s) => s.trim());
  return parts;
}

/**
 * Densely samples an SVG path string into an array of { x, y, t } points.
 * t is the normalised parameter 0..1 along the path length.
 */
export function samplePath(d, numSamples = 300) {
  const { svg, path } = createSvgPath(d);
  const totalLen = path.getTotalLength();
  const points = [];
  for (let i = 0; i <= numSamples; i++) {
    const t = i / numSamples;
    const pt = path.getPointAtLength(t * totalLen);
    points.push({ x: pt.x, y: pt.y, t });
  }
  document.body.removeChild(svg);
  return points;
}

/**
 * Samples multiple subpaths from a single `d` attribute.
 * Returns an array of { subpathIndex, d, points[] } objects.
 */
export function sampleSubpaths(d, numSamples = 300) {
  const subs = getSubpaths(d);
  return subs.map((sub, idx) => ({
    subpathIndex: idx,
    d: sub,
    points: samplePath(sub, numSamples),
  }));
}

/**
 * Finds the sampled point closest to (clickX, clickY).
 * Returns { index, point, distSq } within a single points array.
 */
export function findNearestPoint(samples, clickX, clickY) {
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < samples.length; i++) {
    const dx = samples[i].x - clickX;
    const dy = samples[i].y - clickY;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestDist) {
      bestDist = d2;
      bestIdx = i;
    }
  }
  return { index: bestIdx, point: samples[bestIdx], distSq: bestDist };
}

/**
 * Finds the closest point across all subpaths.
 * Returns { subpathIndex, index, point, distSq, subpath }.
 */
export function findNearestPointAcrossSubpaths(subpathData, clickX, clickY) {
  let best = null;
  for (const sp of subpathData) {
    const hit = findNearestPoint(sp.points, clickX, clickY);
    if (!best || hit.distSq < best.distSq) {
      best = {
        subpathIndex: sp.subpathIndex,
        index: hit.index,
        point: hit.point,
        distSq: hit.distSq,
        subpath: sp,
      };
    }
  }
  return best;
}

export function pointsToD(pts) {
  if (pts.length === 0) return "";
  return "M" + pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" L");
}

/**
 * Given a sampled (closed) contour and two indices, splits into two rail paths.
 * Both rails run in the same direction (from split-point-1 toward split-point-2).
 *
 *   rail0 = points[idx1] → points[idx2]  (the "short way" forward)
 *   rail1 = points[idx2] → end → wrap → points[idx1]  (the "long way" reversed)
 *
 * The reversal ensures both rails are oriented the same way, which Ink/Stitch requires.
 */
export function splitAtIndices(samples, idx1, idx2) {
  const n = samples.length;
  if (n < 4) return null;

  let a = idx1;
  let b = idx2;
  if (a > b) [a, b] = [b, a];

  const forward = samples.slice(a, b + 1);

  const wrap = [...samples.slice(b), ...samples.slice(0, a + 1)];
  const reversed = wrap.slice().reverse();

  if (forward.length < 2 || reversed.length < 2) return null;

  return {
    rail0: pointsToD(forward),
    rail1: pointsToD(reversed),
  };
}

/**
 * High-level: split a subpath's sample array at two parameter values t1, t2.
 */
export function splitAtParams(samples, t1, t2) {
  const find = (t) => {
    let best = 0;
    let bestDiff = Infinity;
    for (let i = 0; i < samples.length; i++) {
      const diff = Math.abs(samples[i].t - t);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = i;
      }
    }
    return best;
  };
  return splitAtIndices(samples, find(t1), find(t2));
}

/**
 * Validates that two split points are far enough apart.
 * Returns true if the arc-distance between them is at least `minFraction` of the total.
 */
export function validateSplitDistance(samples, idx1, idx2, minFraction = 0.08) {
  const n = samples.length;
  if (n < 4) return false;
  const dist = Math.abs(idx1 - idx2);
  const arcDist = Math.min(dist, n - dist);
  return arcDist / n >= minFraction;
}

/**
 * Converts an array of points into a closed SVG `d` string (ending with Z).
 */
export function pointsToClosedD(pts) {
  if (pts.length < 2) return "";
  return "M" + pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" L") + " Z";
}

/**
 * Cuts a closed contour (sampled points) at two indices into two new closed pieces.
 *
 * Piece A: outline from idx1 → idx2 (forward), closed by a straight line back.
 * Piece B: outline from idx2 → wrap → idx1, closed by a straight line back.
 *
 * Returns { pieceA: points[], pieceB: points[] } or null if invalid.
 */
export function cutShapeAtIndices(samples, idx1, idx2) {
  const n = samples.length;
  if (n < 4) return null;

  let a = idx1;
  let b = idx2;
  if (a > b) [a, b] = [b, a];

  const pieceA = samples.slice(a, b + 1);
  const pieceB = [...samples.slice(b), ...samples.slice(0, a + 1)];

  if (pieceA.length < 3 || pieceB.length < 3) return null;

  return { pieceA, pieceB };
}

/**
 * Finds which piece (by index) a click lands on, and the closest sample index within it.
 * `pieces` is an array of { points, d } objects.
 */
export function findNearestPointAcrossPieces(pieces, clickX, clickY) {
  let best = null;
  for (let pi = 0; pi < pieces.length; pi++) {
    const hit = findNearestPoint(pieces[pi].points, clickX, clickY);
    if (!best || hit.distSq < best.distSq) {
      best = { pieceIndex: pi, index: hit.index, point: hit.point, distSq: hit.distSq };
    }
  }
  return best;
}

/**
 * Align two rail paths so they run in the same direction. Samples both paths,
 * compares start-to-start vs start-to-end proximity, and reverses rail1 if
 * they're running in opposite directions.
 *
 * Returns { rail0, rail1, rungs } where `rungs` is an array of SVG path
 * d-strings for auto-generated guide lines showing the stitch direction
 * (evenly spaced perpendicular connections between the two rails).
 */
export function alignRailDirections(rail0D, rail1D, numRungs) {
  const pts0 = samplePath(rail0D, 200);
  const pts1 = samplePath(rail1D, 200);
  if (pts0.length < 2 || pts1.length < 2) return { rail0: rail0D, rail1: rail1D, rungs: [] };

  const s0 = pts0[0], e0 = pts0[pts0.length - 1];
  const s1 = pts1[0], e1 = pts1[pts1.length - 1];

  const sameDir = Math.hypot(s0.x - s1.x, s0.y - s1.y) + Math.hypot(e0.x - e1.x, e0.y - e1.y);
  const revDir = Math.hypot(s0.x - e1.x, s0.y - e1.y) + Math.hypot(e0.x - s1.x, e0.y - s1.y);

  let alignedPts1 = pts1;
  let alignedD1 = rail1D;
  if (revDir < sameDir) {
    alignedPts1 = pts1.slice().reverse();
    alignedD1 = pointsToD(alignedPts1);
  }

  // Compute adaptive rung count based on average rail length if not specified.
  // Longer/more complex paths need more guides to properly define stitch direction.
  if (numRungs == null) {
    const railLen = (pts) => {
      let d = 0;
      for (let j = 1; j < pts.length; j++) d += Math.hypot(pts[j].x - pts[j-1].x, pts[j].y - pts[j-1].y);
      return d;
    };
    const avgLen = (railLen(pts0) + railLen(alignedPts1)) / 2;
    const avgWidth = (() => {
      let sum = 0, count = 0;
      for (let i = 0; i < pts0.length; i += Math.max(1, Math.floor(pts0.length / 10))) {
        const j = Math.min(alignedPts1.length - 1, Math.round(i / (pts0.length - 1) * (alignedPts1.length - 1)));
        sum += Math.hypot(pts0[i].x - alignedPts1[j].x, pts0[i].y - alignedPts1[j].y);
        count++;
      }
      return count > 0 ? sum / count : 50;
    })();
    // Place a rung roughly every 2-3x the satin width, with min 7 and max 40
    const spacing = Math.max(avgWidth * 2, 20);
    numRungs = Math.max(7, Math.min(40, Math.round(avgLen / spacing)));
  }

  // Generate rungs evenly distributed along the rail length.
  // For short satin columns (<=10 rungs), add denser tip rungs.
  // For longer paths, use purely uniform spacing.
  const tValues = [];
  if (numRungs <= 10) {
    tValues.push(0.04, 0.10);
    const innerCount = Math.max(0, numRungs - 4);
    for (let i = 0; i < innerCount; i++) {
      tValues.push(0.15 + (i + 1) * (0.70 / (innerCount + 1)));
    }
    tValues.push(0.90, 0.96);
  } else {
    for (let i = 0; i < numRungs; i++) {
      tValues.push((i + 1) / (numRungs + 1));
    }
  }

  const rungs = [];
  for (const t of tValues) {
    const idx0 = Math.round(t * (pts0.length - 1));
    const idx1 = Math.round(t * (alignedPts1.length - 1));
    const p0 = pts0[idx0];
    const p1 = alignedPts1[idx1];
    if (p0 && p1) {
      rungs.push(`M${p0.x.toFixed(1)},${p0.y.toFixed(1)} L${p1.x.toFixed(1)},${p1.y.toFixed(1)}`);
    }
  }

  return { rail0: rail0D, rail1: alignedD1, rungs };
}

/**
 * Generate concentric satin rails that trace a closed shape's outline, producing
 * a satin *border* (an outline of satin) rather than filling across the shape.
 *
 * The outline is sampled, then offset outward and inward by half the border
 * width along per-vertex normals, giving two parallel rails that run around the
 * perimeter. The border is centred on the outline edge. Both rails share the
 * same point ordering (already direction-aligned) and the loop is closed back to
 * its start so the satin covers the full perimeter.
 *
 * @param {string} pathD          – the shape's `d` attribute (first subpath used)
 * @param {number} borderWidthVb  – border width in SVG viewBox units
 * @param {number} numSamples     – outline sample density
 * @returns {{rail0:string, rail1:string, rungs:string[]}|null}
 */
export function generateSatinBorderRails(pathD, borderWidthVb, numSamples = 400) {
  if (!pathD || !(borderWidthVb > 0)) return null;
  const subs = getSubpaths(pathD);
  if (subs.length === 0) return null;

  const raw = samplePath(subs[0], numSamples);
  if (raw.length < 8) return null;

  // Drop the duplicate closing sample (closed paths end where they start).
  const pts = raw.slice();
  const first = pts[0];
  const last = pts[pts.length - 1];
  if (Math.hypot(first.x - last.x, first.y - last.y) < 1e-3) pts.pop();
  const n = pts.length;
  if (n < 6) return null;

  // Centroid — used to orient normals outward regardless of path winding.
  let cx = 0, cy = 0;
  for (const p of pts) { cx += p.x; cy += p.y; }
  cx /= n; cy /= n;

  // Per-vertex normal (perpendicular to the local tangent).
  const normals = new Array(n);
  for (let i = 0; i < n; i++) {
    const prev = pts[(i - 1 + n) % n];
    const next = pts[(i + 1) % n];
    let tx = next.x - prev.x;
    let ty = next.y - prev.y;
    const len = Math.hypot(tx, ty) || 1;
    tx /= len; ty /= len;
    normals[i] = { x: -ty, y: tx };
  }

  // Pick the global normal sign using the vertex farthest from the centroid
  // (guaranteed to be on the convex hull, where "outward" is unambiguous).
  let farIdx = 0, farDist = -1;
  for (let i = 0; i < n; i++) {
    const d = Math.hypot(pts[i].x - cx, pts[i].y - cy);
    if (d > farDist) { farDist = d; farIdx = i; }
  }
  const outwardDot =
    normals[farIdx].x * (pts[farIdx].x - cx) + normals[farIdx].y * (pts[farIdx].y - cy);
  const sign = outwardDot >= 0 ? 1 : -1;

  const half = borderWidthVb / 2;
  const outer = [];
  const inner = [];
  for (let i = 0; i < n; i++) {
    const nx = normals[i].x * sign;
    const ny = normals[i].y * sign;
    outer.push({ x: pts[i].x + nx * half, y: pts[i].y + ny * half });
    inner.push({ x: pts[i].x - nx * half, y: pts[i].y - ny * half });
  }
  // Close the ring back to the start so the satin wraps the whole perimeter.
  outer.push({ ...outer[0] });
  inner.push({ ...inner[0] });

  const rail0 = pointsToD(outer);
  const rail1 = pointsToD(inner);

  // Rung density scales with perimeter so longer borders get more guides.
  let perim = 0;
  for (let i = 1; i < pts.length; i++) {
    perim += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
  }
  const rungCount = Math.max(8, Math.min(80, Math.round(perim / Math.max(borderWidthVb * 2, 1))));
  const rungs = [];
  const m = outer.length;
  for (let k = 0; k <= rungCount; k++) {
    const idx = Math.round((k / rungCount) * (m - 1));
    const a = outer[idx];
    const b = inner[idx];
    rungs.push(`M${a.x.toFixed(1)},${a.y.toFixed(1)} L${b.x.toFixed(1)},${b.y.toFixed(1)}`);
  }

  return { rail0, rail1, rungs };
}
